import {
	CAP_TABLE_NUMBER_FORMAT,
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { useCallback, useEffect, useState } from 'react';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useDidUpdate } from '@mantine/hooks';
import type { CapChartStoredSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSettings.types.ts';
import {
	DEFAULT_CAP_CHART_SETTINGS,
	orderValuesHeldTypes,
	parseStoredCapChartSettings,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSettings.utils.ts';
import { CapTableRail } from '@dealstack/shared/lib/generated';

export const useCapChartPageHeaderController = (dealId: string) => {
	const [valuesHeldTypes, setValuesHeldTypes] = useState(
		DEFAULT_CAP_CHART_SETTINGS.valuesHeldTypes
	);
	const [valueDisplayMode, setValueDisplayMode] = useState(
		DEFAULT_CAP_CHART_SETTINGS.valueDisplayMode
	);
	const [numberFormat, setNumberFormat] = useState(DEFAULT_CAP_CHART_SETTINGS.numberFormat);
	// CAP-1972: beneficial is the default rail; the toggle is only shown on nominee-structure deals.
	const [rail, setRail] = useState(CapTableRail.BENEFICIAL);

	useEffect(() => {
		const settings = StorageUtilities.getItem<CapChartStoredSettings>(
			StorageKeys.CAP_CHART_LOCAL_STORAGE_SETTINGS_KEY,
			dealId
		);
		if (!settings) {
			return;
		}

		const parsed = parseStoredCapChartSettings(settings);
		setValuesHeldTypes(parsed.valuesHeldTypes);
		setValueDisplayMode(parsed.valueDisplayMode);
		setNumberFormat(parsed.numberFormat);
		// Older persisted settings predate the rail — fall back to the beneficial default.
		setRail(settings.rail ?? CapTableRail.BENEFICIAL);
	}, []);

	useDidUpdate(() => {
		const settings: CapChartStoredSettings = {
			valueDisplayMode,
			valuesHeldTypes,
			numberFormat,
			rail,
		};
		StorageUtilities.setItem(
			StorageKeys.CAP_CHART_LOCAL_STORAGE_SETTINGS_KEY,
			settings,
			dealId
		);
	}, [valueDisplayMode, valuesHeldTypes, numberFormat, rail]);

	const onValuesHeldTypesChange = useCallback((value: CAP_TABLE_VALUES_HELD_TYPE[]) => {
		const ordered = orderValuesHeldTypes(value);
		// At least one value type stays selected; clearing the last one is ignored.
		if (ordered.length) {
			setValuesHeldTypes(ordered);
		}
	}, []);

	const onValueDisplayModeChange = useCallback((value: CAP_TABLE_VALUE_DISPLAY_MODE) => {
		setValueDisplayMode?.(value);
	}, []);

	const onNumberFormatChange = useCallback((value: CAP_TABLE_NUMBER_FORMAT) => {
		setNumberFormat(value);
	}, []);

	const onResetSettings = useCallback(() => {
		setValuesHeldTypes(DEFAULT_CAP_CHART_SETTINGS.valuesHeldTypes);
		setValueDisplayMode(DEFAULT_CAP_CHART_SETTINGS.valueDisplayMode);
		setNumberFormat(DEFAULT_CAP_CHART_SETTINGS.numberFormat);
	}, []);

	return {
		valueDisplayMode,
		valuesHeldTypes,
		numberFormat,
		onValuesHeldTypesChange,
		onValueDisplayModeChange,
		onNumberFormatChange,
		onResetSettings,
		rail,
		setRail,
	};
};
